import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
`;

const Button = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    font-size: 12px;
    width: 100%;
    border-radius: 6px;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  color: #667eea;
  border: 1px solid #667eea;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #667eea;
    color: white;
  }
`;

const DangerButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  color: #e74c3c;
  border: 1px solid #e74c3c;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e74c3c;
    color: white;
  }
`;

const Table = styled.table`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  overflow: hidden;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: rgba(102, 126, 234, 0.2);
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: rgba(255, 255, 255, 0.02);
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const TableCell = styled.td`
  padding: 16px;
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 14px;
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  color: white;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const ActionCell = styled(TableCell)`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 95%;
    padding: 1.5rem;
    margin: 1rem;
    max-height: 85vh;
  }
  
  @media (max-width: 480px) {
    width: 98%;
    padding: 1rem;
    margin: 0.5rem;
    border-radius: 12px;
  }
`;

const ModalHeader = styled.h3`
  color: #333;
  margin-bottom: 2.5rem;
  font-size: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 1.5rem;
    text-align: center;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1.2rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 14px;
  
  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    margin-bottom: 5px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 12px;
    border-radius: 6px;
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  box-sizing: border-box;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 13px;
    min-height: 70px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 12px;
    min-height: 60px;
    border-radius: 6px;
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    gap: 10px;
    margin-top: 1.5rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    margin-top: 1rem;
  }
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
  
  ${({ $success }) => $success && `
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `}
  
  ${({ $error }) => $error && `
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
    margin-top: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.6rem;
    margin-top: 1rem;
  }
`;

const FullWidthGroup = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const ExpertManagement = ({ showMessage }) => {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentExpert, setCurrentExpert] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    bio: '',
    qualification: '',
    industry_experience: ''
  });

  const fetchExperts = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/experts`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setExperts(data.data || []);
      } else {
        throw new Error('Failed to fetch experts');
      }
    } catch (error) {
      console.error('Error fetching experts:', error);
      setLocalMessage('Error fetching experts', 'error');
    }
  }, []);

  useEffect(() => {
    fetchExperts();
  }, [fetchExperts]);

  const setLocalMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateExpert = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      bio: '',
      qualification: '',
      industry_experience: ''
    });
    setShowCreateModal(true);
  };

  const handleEditExpert = (expert) => {
    setCurrentExpert(expert);
    setFormData({
      bio: expert.bio || '',
      qualification: expert.qualification || '',
      industry_experience: expert.industry_experience || ''
    });
    setShowEditModal(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/experts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setLocalMessage('Expert created successfully!', 'success');
        setShowCreateModal(false);
        fetchExperts();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to create expert');
      }
    } catch (error) {
      console.error('Error creating expert:', error);
      setLocalMessage(error.message || 'Failed to create expert', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/expert/${currentExpert.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setLocalMessage('Expert profile updated successfully!', 'success');
        setShowEditModal(false);
        fetchExperts();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to update expert');
      }
    } catch (error) {
      console.error('Error updating expert:', error);
      setLocalMessage(error.message || 'Failed to update expert', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Remove unused function
  // const handlePromoteToExpert = async (userId) => {
  //   if (!window.confirm('Are you sure you want to promote this user to expert?')) {
  //     return;
  //   }
  //
  //   try {
  //     const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
  //     const response = await fetch(`${API_BASE_URL}/api/v1/admin/expert/promote/${userId}`, {
  //       method: 'PATCH',
  //       headers: {
  //         'Authorization': token ? `Bearer ${token}` : '',
  //       }
  //     });
  //
  //     if (response.ok) {
  //       setLocalMessage('User promoted to expert successfully!', 'success');
  //       fetchExperts();
  //     } else {
  //       const errorData = await response.json();
  //       throw new Error(errorData.error?.message || 'Failed to promote user');
  //     }
  //   } catch (error) {
  //     console.error('Error promoting user:', error);
  //     setLocalMessage(error.message || 'Failed to promote user', 'error');
  //   }
  // };

  const handleRemoveExpertRole = async (userId) => {
    if (!window.confirm('Are you sure you want to remove expert role from this user? This will also delete their expert profile.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/expert/remove/${userId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });

      if (response.ok) {
        setLocalMessage('Expert role removed successfully!', 'success');
        fetchExperts();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to remove expert role');
      }
    } catch (error) {
      console.error('Error removing expert role:', error);
      setLocalMessage(error.message || 'Failed to remove expert role', 'error');
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setCurrentExpert(null);
    setMessage('');
  };

  return (
    <Container>
      <Header>
        <Title>Expert Management</Title>
        <Button onClick={handleCreateExpert}>Create Expert</Button>
      </Header>

      {message && (
        <Message $success={messageType === 'success'} $error={messageType === 'error'}>
          {message}
        </Message>
      )}

      <Table>
        <TableHeader>
          <tr>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>Email</TableHeaderCell>
            <TableHeaderCell>Bio</TableHeaderCell>
            <TableHeaderCell>Qualification</TableHeaderCell>
            <TableHeaderCell>Experience</TableHeaderCell>
            <TableHeaderCell>Actions</TableHeaderCell>
          </tr>
        </TableHeader>
        <tbody>
          {experts.map((expert) => (
            <TableRow key={expert.id}>
              <TableCell>{expert.full_name || 'N/A'}</TableCell>
              <TableCell>{expert.email}</TableCell>
              <TableCell>{expert.bio ? `${expert.bio.substring(0, 50)}...` : 'N/A'}</TableCell>
              <TableCell>{expert.qualification || 'N/A'}</TableCell>
              <TableCell>{expert.industry_experience ? `${expert.industry_experience} years` : 'N/A'}</TableCell>
              <ActionCell>
                <SecondaryButton onClick={() => handleEditExpert(expert)}>
                  Edit Profile
                </SecondaryButton>
                <DangerButton onClick={() => handleRemoveExpertRole(expert.id)}>
                  Remove Role
                </DangerButton>
              </ActionCell>
            </TableRow>
          ))}
          {experts.length === 0 && (
            <TableRow>
              <TableCell colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                No experts found
              </TableCell>
            </TableRow>
          )}
        </tbody>
      </Table>

      {/* Create Expert Modal */}
      {showCreateModal && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>Create New Expert</ModalHeader>
            {message && (
              <Message $success={messageType === 'success'} $error={messageType === 'error'}>
                {message}
              </Message>
            )}
            <form onSubmit={handleCreateSubmit} style={{ marginTop: '1rem' }}>
              <FormContainer>
                <FormGroup>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    required
                    minLength="8"
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="qualification">Qualification</Label>
                  <Input
                    id="qualification"
                    type="text"
                    value={formData.qualification}
                    onChange={(e) => handleInputChange('qualification', e.target.value)}
                    placeholder="Academic and professional qualifications"
                    maxLength="1000"
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="industry_experience">Industry Experience (Years)</Label>
                  <Input
                    id="industry_experience"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.industry_experience}
                    onChange={(e) => handleInputChange('industry_experience', e.target.value)}
                    placeholder="Years of industry experience"
                  />
                </FormGroup>
                <FullWidthGroup>
                  <Label htmlFor="bio">Bio</Label>
                  <TextArea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder="Professional biography..."
                    maxLength="2000"
                  />
                </FullWidthGroup>
              </FormContainer>
              <ButtonGroup>
                <Button type="button" onClick={closeModal}>Cancel</Button>
                <Button type="submit" disabled={loading}>
                  {loading ? <LoadingSpinner /> : 'Create Expert'}
                </Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* Edit Expert Modal */}
      {showEditModal && currentExpert && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>Edit Expert Profile</ModalHeader>
            {message && (
              <Message $success={messageType === 'success'} $error={messageType === 'error'}>
                {message}
              </Message>
            )}
            <form onSubmit={handleEditSubmit} style={{ marginTop: '1rem' }}>
              <FormContainer>
                <FormGroup>
                  <Label htmlFor="edit_qualification">Qualification</Label>
                  <Input
                    id="edit_qualification"
                    type="text"
                    value={formData.qualification}
                    onChange={(e) => handleInputChange('qualification', e.target.value)}
                    placeholder="Academic and professional qualifications"
                    maxLength="1000"
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="edit_industry_experience">Industry Experience (Years)</Label>
                  <Input
                    id="edit_industry_experience"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.industry_experience}
                    onChange={(e) => handleInputChange('industry_experience', e.target.value)}
                    placeholder="Years of industry experience"
                  />
                </FormGroup>
                <FullWidthGroup>
                  <Label htmlFor="edit_bio">Bio</Label>
                  <TextArea
                    id="edit_bio"
                    value={formData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder="Professional biography..."
                    maxLength="2000"
                  />
                </FullWidthGroup>
              </FormContainer>
              <ButtonGroup>
                <Button type="button" onClick={closeModal}>Cancel</Button>
                <Button type="submit" disabled={loading}>
                  {loading ? <LoadingSpinner /> : 'Update Profile'}
                </Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default ExpertManagement;
